// "Meine Aufgaben" — cross-project task hub (ADR D8 Phase 1).
//
// One workspace over (a) every project task assigned to me and (b) every task
// in my accessible lists, rendered through the unified List / Board / Calendar
// views with grouping, multi-sort, search and persisted ViewState ("me:tasks").
// Quick-add creates a personal task by default (no project needed). Checking a
// box off works cross-source: personal → toggle; project → flip to the project's
// done/open status via the normal PATCH path (access + history intact).
//
// Redesign-Paket 2: Titel und Ansichts-Umschalter stehen nicht mehr im Inhalt,
// sondern in der EINEN App-Kopfzeile (`usePageHeader`); der Umschalter ist dort
// die Tab-Leiste (`ViewTabs`) statt des Segment-Reglers (`ViewSwitcher`) —
// gleiche drei Ansichten, gleiche Reihenfolge, gleiches Verhalten. Der Inhalt
// liegt in `PageShell` (vorher 1152px, jetzt die eine Breite von 1440px).

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { CalendarDays, Columns3, List, Plus } from "lucide-preact";
import type { PersonalTask } from "@juuplan/shared";
import { Layout } from "../components/Layout";
import { openPersonalTaskSig, openProjectTaskSig } from "../store/deepLinks";
import { uxEnabled } from "../lib/ux";
import { displayOrder, neighbourUid } from "../lib/taskDetailModel";
import { computeView } from "../lib/viewState";
import { TaskDetailPanel } from "../components/taskDetail/TaskDetailPanel";
import { DetailSheet } from "../components/taskDetail/DetailSheet";
import { api, batchUpdateTasks } from "../lib/api";
import { applyHubChange, buildProjectMeta, type MeTask as HubMeTask } from "../lib/hubContext";
import { splitBulkAssigneeEligible } from "../components/BulkAssigneeAction";
import { BulkActionToolbar } from "../components/BulkActionToolbar";
import { GroupSortFilterBar } from "../components/GroupSortFilterBar";
import { SavedViewsMenu } from "../components/SavedViewsMenu";
import { PageShell } from "../components/ui/PageShell";
import { ViewTabs } from "../components/ui/ViewTabs";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { TaskListView } from "../views/TaskListView";
import { TaskBoardView } from "../views/TaskBoardView";
import { TaskCalendarView } from "../views/TaskCalendarView";
import {
  personalTaskToRecord,
  projectTaskToRecordWithProject,
  type ResolverContext,
  type StatusOption,
  type TaskRecord,
  type TaskRecordChange,
} from "../lib/taskRecord";
import { loadViewState, saveViewState } from "../lib/viewStateStore";
import { DEFAULT_VIEW_STATE, type GroupBy, type SortKey, type ViewKind, type ViewState } from "../lib/viewState";
import { useUsers, findUser } from "../store/users";
import { createPersonalTask, deletePersonalTask } from "../store/personalTasks";
import { deleteTask } from "../store/project";
import { toastError, toastSuccess } from "../lib/toast";

type MeTask = HubMeTask;

const SCOPE = "me:tasks";
const HUB_DEFAULT: ViewState = {
  ...DEFAULT_VIEW_STATE,
  groupBy: "project",
  sort: [{ key: "due", dir: "asc" }],
};

const GROUP_OPTS: GroupBy[] = ["none", "project", "status", "priority", "assignee", "due"];
const SORT_OPTS: SortKey[] = ["manual", "due", "priority", "title", "status", "updated"];

/** Dieselben drei Ansichten in derselben Reihenfolge wie bisher im
 *  `ViewSwitcher` — nur als Tab-Leiste im Seitenkopf gerendert. */
const VIEW_TABS: { id: ViewKind; label: string; icon: typeof List }[] = [
  { id: "list", label: "Liste", icon: List },
  { id: "board", label: "Board", icon: Columns3 },
  { id: "calendar", label: "Kalender", icon: CalendarDays },
];

type DoneFilter = "open" | "all" | "done";
const DONE_FILTERS: { id: DoneFilter; label: string }[] = [
  { id: "open", label: "Offen" },
  { id: "all", label: "Alle" },
  { id: "done", label: "Erledigt" },
];

export function MyTasks(): preact.JSX.Element {
  const [, setLocation] = useLocation();
  useUsers(); // warm the user cache for assignee resolution

  const [projectTasks, setProjectTasks] = useState<MeTask[]>([]);
  const [personalTasks, setPersonalTasks] = useState<PersonalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [state, setState] = useState<ViewState>(() => loadViewState(SCOPE, HUB_DEFAULT));
  const [doneFilter, setDoneFilter] = useState<DoneFilter>("open");
  const [quickAdd, setQuickAdd] = useState("");
  const [adding, setAdding] = useState(false);
  const quickRef = useRef<HTMLInputElement>(null);
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (uid: string): void =>
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const updateState = (next: ViewState): void => {
    setState(next);
    saveViewState(SCOPE, next);
  };

  const reload = (): void => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api<MeTask[]>("/api/me/tasks"),
      api<PersonalTask[]>("/api/personal-tasks?scope=accessible_lists&include_done=true"),
    ]).then(
      ([proj, pers]) => {
        if (cancelled) return;
        setProjectTasks(proj);
        setPersonalTasks(pers);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Unbekannter Fehler");
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Project lookup + status ids/sets, and the one change path — shared with
  // the P3 detail panel in „Mein Tag" (lib/hubContext.ts).
  const projectMeta = useMemo(() => buildProjectMeta(projectTasks), [projectTasks]);
  const applyChange = (record: TaskRecord, change: TaskRecordChange): Promise<boolean> =>
    applyHubChange(record, change, projectMeta);

  const ctx: ResolverContext = useMemo(
    () => ({
      resolveProject: (id) => projectMeta.projects.get(id),
      resolvePhase: () => undefined,
      resolveLabel: () => undefined,
      resolveUserName: (id) => findUser(id)?.name ?? `User #${id}`,
      // Project tasks: offer the project's full status set (offen/in Arbeit/
      // erledigt …). Personal tasks fall back to the done toggle (no status set
      // is carried in this cross-project payload).
      resolveStatuses: (record): StatusOption[] =>
        record.source === "project" && record.projectId !== null
          ? projectMeta.statusSet.get(record.projectId) ?? []
          : [],
      patch: async (record: TaskRecord, change: TaskRecordChange): Promise<void> => {
        try {
          const ok = await applyChange(record, change);
          if (!ok) {
            toastError("Kein passender Status im Projekt — bitte in der Aufgabe ändern.");
            return;
          }
          reload();
        } catch {
          toastError("Änderung fehlgeschlagen");
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectMeta]
  );

  const records: TaskRecord[] = useMemo(() => {
    const out: TaskRecord[] = [];
    for (const t of projectTasks) out.push(projectTaskToRecordWithProject(t, t.project_id));
    for (const pt of personalTasks) out.push(personalTaskToRecord(pt));
    return out;
  }, [projectTasks, personalTasks]);

  const visibleRecords = useMemo(() => {
    if (doneFilter === "all") return records;
    return records.filter((r) => (doneFilter === "done" ? r.done : !r.done));
  }, [records, doneFilter]);

  const selectedRecords = useMemo(
    () => visibleRecords.filter((r) => selectedUids.has(r.uid)),
    [visibleRecords, selectedUids]
  );

  const bulkApply = async (change: TaskRecordChange): Promise<void> => {
    if (bulkBusy || selectedRecords.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(selectedRecords.map((r) => applyChange(r, change)));
      const failed = results.filter((r) => r.status === "rejected" || r.value === false).length;
      if (failed > 0) toastError(`${failed} Aufgabe(n) konnten nicht aktualisiert werden`);
      setSelectedUids(new Set());
      reload();
    } finally {
      setBulkBusy(false);
    }
  };

  // Bulk-replace the assignee set on the selected PROJECT tasks (#878). The
  // selection here can mix project tasks with personal tasks (no assignees at
  // all) and span several projects — `splitBulkAssigneeEligible` drops the
  // personal ones (reporting how many were skipped instead of silently
  // ignoring or failing on them) and `BulkAssigneeAction`'s `projectIds` prop
  // handles the multi-project picker scoping.
  const bulkApplyAssignees = async (ids: number[]): Promise<void> => {
    if (bulkBusy) return;
    const { eligible, skippedCount } = splitBulkAssigneeEligible(selectedRecords);
    if (eligible.length === 0) {
      if (skippedCount > 0) {
        toastError(
          `${skippedCount} persönliche Aufgabe(n) haben keine Zuständigkeit – nichts zu aktualisieren`
        );
      }
      setSelectedUids(new Set());
      return;
    }
    setBulkBusy(true);
    try {
      await batchUpdateTasks({ ids: eligible.map((r) => r.id), patch: { assignee_ids: ids } });
      const skippedNote =
        skippedCount > 0
          ? ` · ${skippedCount} persönliche Aufgabe(n) übersprungen (keine Zuständigkeit)`
          : "";
      toastSuccess(
        `${eligible.length} ${eligible.length === 1 ? "Aufgabe" : "Aufgaben"} aktualisiert${skippedNote}`
      );
      setSelectedUids(new Set());
      reload();
    } catch {
      toastError("Massenbearbeitung fehlgeschlagen – keine Änderung gespeichert");
    } finally {
      setBulkBusy(false);
    }
  };

  // Deep-links must go through the module signals — Project/Lists never parse
  // a ?task= query param (wouter's useLocation is pathname-only).
  const openFull = (record: TaskRecord): void => {
    if (record.source === "project" && record.projectId !== null) {
      openProjectTaskSig.value = record.id;
      setLocation(`/p/${record.projectId}`);
    } else {
      openPersonalTaskSig.value = record.id;
      setLocation("/lists");
    }
  };

  // UX-Vorschlag P3: Detail rechts neben der Liste statt Seitenwechsel; ohne
  // Flag oeffnet ein Klick wie bisher das Projekt bzw. die Liste.
  const p3 = uxEnabled("ux_web_p3");
  const [detailUid, setDetailUid] = useState<string | null>(null);
  const onOpen = (record: TaskRecord): void => {
    if (p3) setDetailUid(record.uid);
    else openFull(record);
  };
  const detailRecord = p3 && detailUid !== null
    ? visibleRecords.find((r) => r.uid === detailUid) ?? records.find((r) => r.uid === detailUid) ?? null
    : null;
  // ↑/↓ folgt der ANGEZEIGTEN Reihenfolge: die Liste meldet sie (inkl.
  // eingeklappter Gruppen); Board/Kalender nehmen Gruppen × Sortierung.
  const [listOrder, setListOrder] = useState<string[] | null>(null);
  const navigateDetail = (dir: 1 | -1): void => {
    const order =
      state.view === "list" && listOrder !== null
        ? listOrder
        : displayOrder(computeView(visibleRecords, state, bucketOpts), new Set(), state.groupBy === "none");
    setDetailUid(neighbourUid(order, detailUid, dir));
  };

  const onDeleteRecord = async (record: TaskRecord): Promise<void> => {
    if (!window.confirm(`Aufgabe „${record.title}" in den Papierkorb verschieben?`)) return;
    try {
      if (record.source === "personal") await deletePersonalTask(record.id);
      else await deleteTask(record.id);
      reload();
      toastSuccess("Aufgabe gelöscht");
    } catch {
      toastError("Löschen fehlgeschlagen – bitte erneut versuchen");
    }
  };

  const submitQuickAdd = async (): Promise<void> => {
    const title = quickAdd.trim();
    if (!title || adding) return;
    setAdding(true);
    try {
      // Dieser Hub hat keinen Listen-Kontext: `list_id: null` heisst „Server,
      // waehle die Zielliste" (Standard-Liste, sonst erste eigene Liste). Seit
      // Migration 0092 entsteht daraus KEINE Aufgabe ohne Liste mehr.
      await createPersonalTask({ title, list_id: null });
      setQuickAdd("");
      reload();
      quickRef.current?.focus();
    } catch {
      toastError("Aufgabe konnte nicht angelegt werden");
    } finally {
      setAdding(false);
    }
  };

  const bucketOpts = { resolveProject: ctx.resolveProject, resolveUserName: ctx.resolveUserName };

  // Der alte Seitenkopf hatte KEINE Aktion (nur Titel + Untertitel) — die
  // Kopfzeile hat deshalb auch keine: vorher 0, nachher 0. Was umzieht, ist der
  // Ansichts-Umschalter aus der Werkzeugleiste; er gehört laut Referenz
  // (docs/design/01-roadmap-expanded.png) in den Kopf.
  usePageHeader({
    title: requireLabel("/my-tasks"),
    subtitle:
      "Alles an einem Ort — zugewiesene Projekt-Aufgaben und deine persönlichen Aufgaben.",
    tabs: (
      <ViewTabs
        label="Ansicht wählen"
        items={VIEW_TABS}
        active={state.view}
        onChange={(id) => updateState({ ...state, view: id as ViewKind })}
      />
    ),
  });

  return (
    <Layout>
      <PageShell width="default">

        {/* Quick-add → personal task */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitQuickAdd();
          }}
          class="mb-3 flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]"
        >
          <Plus size={16} class="text-[color:var(--color-on-surface-variant)] shrink-0" aria-hidden="true" />
          <input
            ref={quickRef}
            type="text"
            value={quickAdd}
            onInput={(e) => setQuickAdd((e.target as HTMLInputElement).value)}
            placeholder="Schnell eine persönliche Aufgabe hinzufügen…"
            aria-label="Neue persönliche Aufgabe"
            maxLength={200}
            disabled={adding}
            class="flex-1 min-w-0 bg-transparent text-ellipsis text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none"
          />
          {quickAdd.trim() && (
            <span class="hidden sm:inline text-label text-[color:var(--color-on-surface-variant)]">→ persönliche Aufgabe</span>
          )}
        </form>

        {/* Werkzeugleiste — der Ansichts-Umschalter sitzt jetzt im Seitenkopf. */}
        <div class="mb-4 flex flex-wrap items-center gap-2">
          <GroupSortFilterBar state={state} onChange={updateState} groupByOptions={GROUP_OPTS} sortOptions={SORT_OPTS} />
          <SavedViewsMenu scopeKey={SCOPE} current={state} onApply={updateState} />
          <div class="inline-flex items-center rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-0.5 ml-auto">
            {DONE_FILTERS.map((f) => {
              const active = doneFilter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setDoneFilter(f.id)}
                  class={`rounded px-2.5 py-1 text-xs font-medium min-h-[26px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                    active
                      ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                      : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                  }`}
                  aria-pressed={active}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bulk action bar — no status/phase actions here: the selection can
         *  mix personal and project tasks across several projects, so there
         *  is no single status list or phase set to offer. */}
        <BulkActionToolbar
          count={selectedUids.size}
          busy={bulkBusy}
          onSetDone={() => void bulkApply({ done: true })}
          onSetPriority={(p) => void bulkApply({ priority: p })}
          onSetAssignees={(ids) => void bulkApplyAssignees(ids)}
          projectIds={selectedRecords
            .filter((r) => r.source === "project" && r.projectId !== null)
            .map((r) => r.projectId as number)}
          onClear={() => setSelectedUids(new Set())}
        />

        {loading ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>
        ) : error ? (
          <div
            role="alert"
            class="flex flex-wrap items-center gap-3 rounded border border-[color:var(--color-error-container)] bg-[color:var(--color-error-container)] px-3 py-2 text-sm text-[color:var(--color-on-error-container)]"
          >
            <span class="flex-1 min-w-0">Aufgaben konnten nicht geladen werden: {error}</span>
            <button
              type="button"
              onClick={reload}
              class="shrink-0 rounded border border-[color:var(--color-on-error-container)]/40 px-2 py-1 text-xs font-medium text-[color:var(--color-on-error-container)] hover:bg-[color:var(--color-on-error-container)]/10"
            >
              Erneut versuchen
            </button>
          </div>
        ) : state.view === "board" ? (
          <TaskBoardView records={visibleRecords} state={state} ctx={ctx} bucketOpts={bucketOpts} onOpenRecord={onOpen} emptyLabel="Keine Aufgaben" />
        ) : state.view === "calendar" ? (
          <TaskCalendarView records={visibleRecords} state={state} ctx={ctx} onOpenRecord={onOpen} />
        ) : (
          <TaskListView
            records={visibleRecords}
            state={state}
            ctx={ctx}
            bucketOpts={bucketOpts}
            onOpenRecord={onOpen}
            onDeleteRecord={onDeleteRecord}
            selectedUids={selectedUids}
            onToggleSelect={toggleSelect}
            onDisplayOrder={p3 ? setListOrder : undefined}
            emptyLabel="Keine Aufgaben — leg oben eine an."
          />
        )}
        {/* Detail als Inlay mittig ueber abgedunkelter Seite (#1254). */}
        {detailRecord && (
          <DetailSheet onClose={() => setDetailUid(null)}>
            <TaskDetailPanel
              record={detailRecord}
              ctx={ctx}
              onClose={() => setDetailUid(null)}
              onNavigate={navigateDetail}
              onOpenFull={openFull}
              onChanged={reload}
            />
          </DetailSheet>
        )}
      </PageShell>
    </Layout>
  );
}
